import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getAge } from "@/utils/dateUtil";
import { DetailedCitizenProceduresIndicatorC3Data } from "@/services/esus/indicator/esf/c3/getDetailedCitizensProceduresIndicatorC3";
import { getCitizenScoreStatusDescriptionIndicatorC3 } from "@/utils/indicatorsEsf";

import { IndicatorEsfC3CitizensProceduresFilters } from "./IndicatorEsfC3CitizensProceduresFilters";
import { IndicatorEsfC3CitizensProceduresTable } from "./IndicatorEsfC3CitizensProceduresTable";
import { IndicatorEsfC3CitizensProceduresHistory } from "./IndicatorEsfC3CitizensProceduresHistory";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC3CitizensProceduresProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedCitizensProcedures: Paginated<DetailedCitizenProceduresIndicatorC3Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllDetailedCitizensProceduresIndicatorC3: () => Promise<
		DetailedCitizenProceduresIndicatorC3Data[]
	>;
};

export function IndicatorEsfC3CitizensProcedures({
	selectedQuarter,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedCitizensProcedures,
	pageNumber,
	setPageNumber,
	handleGetAllDetailedCitizensProceduresIndicatorC3: handleGetAllDetailedIndicatorC1,
}: Readonly<IndicatorEsfC3CitizensProceduresProps>) {
	const [isCitizenHistoryModalOpen, setIsCitizenHistoryModalOpen] = useState(false);
	const [citizenWithProcedures, setCitizenWithProcedures] =
		useState<DetailedCitizenProceduresIndicatorC3Data>(
			new DetailedCitizenProceduresIndicatorC3Data()
		);
	const [isLoadingCitizenWithAcsHomeVisits, setIsLoadingCitizenWithAcsHomeVisits] =
		useState(false);
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleGetCitizenWithProcedures(
		citizenWithHomeVisits: DetailedCitizenProceduresIndicatorC3Data
	) {
		setIsLoadingCitizenWithAcsHomeVisits(true);
		setCitizenWithProcedures(citizenWithHomeVisits);
		setIsLoadingCitizenWithAcsHomeVisits(false);
		setIsCitizenHistoryModalOpen(true);
	}

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const attendances = await handleGetAllDetailedIndicatorC1();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(attendances).length > 0) {
			const excelData: GenericObject[] =
				transformCitizensWithProceduresToGenericObjectArray(attendances);
			exportExcel(
				excelData,
				`Exames e procedimentos - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformCitizensWithProceduresToGenericObjectArray(
		citizensWithProcedures: DetailedCitizenProceduresIndicatorC3Data[]
	) {
		return citizensWithProcedures.map(
			(citizenWithProcedures) =>
				({
					Nome: citizenWithProcedures.fullName,
					DN: dayjs(citizenWithProcedures.birthdate).format("DD/MM/YYYY"),
					Idade: getAge(citizenWithProcedures.birthdate),
					Documento: citizenWithProcedures.cpf ?? citizenWithProcedures.cns ?? "",
					Equipe: citizenWithProcedures.healthTeam?.name ?? "",
					Ine: citizenWithProcedures.healthTeam?.ine ?? "",
					Microarea: citizenWithProcedures.area ?? "FA",
					Dum: dayjs(citizenWithProcedures.dumDate).format("DD/MM/YYYY"),
					Dpp: dayjs(citizenWithProcedures.dppDate).format("DD/MM/YYYY"),
					IG: citizenWithProcedures.gestationalAge ?? "",
					"HIV 1º Trimestre": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenWithProcedures.firstTrimesterHivProceduresStatus
					),
					"Sífilis 1º Trimestre": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenWithProcedures.firstTrimesterSifilisProceduresStatus
					),
					"Hepatite B 1º Trimestre": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenWithProcedures.firstTrimesterHepBProceduresStatus
					),
					"Hepatite C 1º Trimestre": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenWithProcedures.firstTrimesterHepCProceduresStatus
					),
					"Status Vacinas 1º Trimestre": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenWithProcedures.firstTrimesterProceduresStatus
					),
					"HIV 3º Trimestre": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenWithProcedures.thirdTrimesterHivProceduresStatus
					),
					"Sífilis 3º Trimestre": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenWithProcedures.thirdTrimesterSifilisProceduresStatus
					),
					"Status Vacinas 3º Trimestre": getCitizenScoreStatusDescriptionIndicatorC3(
						citizenWithProcedures.thirdTrimesterProceduresStatus
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingCitizenWithAcsHomeVisits || isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC3CitizensProceduresFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC3CitizensProceduresTable
							citizensProcedures={paginatedCitizensProcedures.data}
							isLoading={isLoading}
							openCitizenHistory={handleGetCitizenWithProcedures}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedCitizensProcedures?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedCitizensProcedures.pageSize}
								totalPages={paginatedCitizensProcedures.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
				<Modal
					show={isCitizenHistoryModalOpen}
					onHide={() => setIsCitizenHistoryModalOpen(false)}
					size="xl"
					aria-labelledby="contained-modal-title-vcenter"
					centered
				>
					<Modal.Header closeButton>
						<Modal.Title>
							<h3 className="text-uppercase text-dark fw-bold fs-7">
								Histórico de exames e procedimentos
							</h3>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<IndicatorEsfC3CitizensProceduresHistory
							citizenWithAttendanceProcedures={citizenWithProcedures}
						/>
					</Modal.Body>
				</Modal>
			</Col>
		</Row>
	);
}
